/**
 * Lazy loader for the Google Maps JS API using the NEW Places API (v2).
 * Uses importLibrary() — the modern bootstrap pattern that avoids
 * the legacy `libraries=places` query param which triggers LegacyApiNotActivatedMapError.
 */
let mapsPromise = null;

export function loadGoogleMaps() {
  if (typeof window === "undefined") return Promise.resolve(null);

  // Already fully loaded
  if (window.google?.maps?.importLibrary) {
    return mapsPromise || (mapsPromise = window.google.maps.importLibrary("places").then(() => window.google));
  }

  if (mapsPromise) return mapsPromise;

  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) {
    return Promise.reject(new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY"));
  }

  mapsPromise = new Promise((resolve, reject) => {
    // Surface auth errors
    window.gm_authFailure = () => {
      mapsPromise = null;
      console.error(
        "[Google Maps] Auth failed. Ensure the Maps JavaScript API and Places API (New) are enabled for this key, and HTTP referrer restrictions allow this domain."
      );
      reject(new Error("Google Maps auth failed — see console"));
    };

    // Inject the bootstrap script (no libraries= param — importLibrary handles it)
    const id = "google-maps-script";
    if (!document.getElementById(id)) {
      const script = document.createElement("script");
      script.id = id;
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&v=weekly&loading=async`;
      script.onerror = (e) => { mapsPromise = null; reject(e); };
      document.head.appendChild(script);
    }

    // Poll until importLibrary is available, then load places
    const poll = setInterval(async () => {
      if (!window.google?.maps?.importLibrary) return;
      clearInterval(poll);
      try {
        await window.google.maps.importLibrary("places");
        resolve(window.google);
      } catch (e) {
        mapsPromise = null;
        reject(e);
      }
    }, 80);

    setTimeout(() => {
      clearInterval(poll);
      if (!window.google?.maps?.importLibrary) {
        mapsPromise = null;
        reject(new Error("Google Maps failed to load within 15s"));
      }
    }, 15000);
  });

  return mapsPromise;
}
